import React, { Component, type UIEvent } from 'react';
import { styled, css } from '@compiled/react';
import flow from 'lodash/flow';
import noop from 'lodash/noop';
import Button from '@atlaskit/button';
import StarredIcon from '@atlaskit/icon/core/star-starred';
import UnstarredIcon from '@atlaskit/icon/core/star-unstarred';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import type { UIAnalyticsEvent as AnalyticsEvent } from '@atlaskit/analytics-next';
import withFireUiAnalytics from '@atlassian/jira-analytics-web-react/src/components/with-fire-ui-analytics.tsx';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { injectIntl } from '@atlassian/jira-intl';
import type { Intl } from '@atlassian/jira-shared-types/src/general.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import type { Props as OwnProps } from '../model/types.tsx';
import messages from './messages.tsx';

type Props = Intl & OwnProps;

const SKELETON_TEST_ID = 'favouriting.favorite-button-stateless.skeleton';
const ICON_WRAPPER_TEST_ID_PREFIX = 'favouriting.favorite-button-stateless.icon-wrapper';

// eslint-disable-next-line jira/react/no-class-components
export class FavoriteButtonStateless extends Component<
	Props,
	{
		isAnimating: boolean;
	}
> {
	static defaultProps = {
		isFavorite: undefined,
		favoriteItemName: undefined,
		isShownInList: false,
		isSmall: false,
		hideTooltip: false,
		onClick: noop,
		tooltipPosition: 'bottom',
		skipAnimation: false,
	};

	state = {
		isAnimating: false,
	};

	componentDidUpdate(prevProps: Props) {
		if (
			!this.props.skipAnimation &&
			((!prevProps.pending && this.props.pending) ||
				(!prevProps.isFavorite && this.props.isFavorite))
		) {
			this.onAnimationStart();
			this.onSettingAnimationEnd();
		}
	}

	onBtnClickWrapper = (e: UIEvent<HTMLSpanElement>) => {
		e.stopPropagation(); // should not let this event get outside of the component, to prevent issue when, for example, star button is clicked in an item or row, which goes somewhere else on onClick handler. Cannot put it in onBtnClick, otherwise hideTooltipOnClick in <Tooltip> will not work
		e.preventDefault(); // same for cases when btn is inside a link - not to go to that link (e.g. in Star Drawer)
	};

	onClick = (_: UIEvent<HTMLButtonElement>, analyticsEvent: AnalyticsEvent) => {
		this.props.onClick(analyticsEvent);
	};

	onAnimationStart() {
		this.setState({
			isAnimating: !this.state.isAnimating,
		});
	}

	onSettingAnimationEnd() {
		setTimeout(() => {
			this.setState({
				isAnimating: false,
			});
		}, animationTime * 2);
	}

	render() {
		const {
			isFavorite,
			favoriteItemName,
			isShownInList,
			isSmall,
			hideTooltip,
			tooltipPosition,
			pending,
			intl: { formatMessage },
		} = this.props;

		const { isAnimating } = this.state;

		if (isFavorite === undefined) {
			// show skeleton as a space placeholder (to avoid layout recalculations in browser and visible "jump" when we got data)
			return (
				<UFOLoadHold name="favourite-button">
					<FavoriteButtonSkeleton data-testid={SKELETON_TEST_ID} isSmall={isSmall} />
				</UFOLoadHold>
			);
		}

		const iconWrapperTestId = isShownInList
			? `${ICON_WRAPPER_TEST_ID_PREFIX}-list`
			: ICON_WRAPPER_TEST_ID_PREFIX;

		const buttonContentOld = (
			<FavIconWrapper
				isFavorite={isFavorite}
				isAnimating={isAnimating}
				data-testid={iconWrapperTestId}
				isSmall={isSmall}
			>
				{isFavorite ? (
					<StarredIcon
						LEGACY_size={isSmall ? 'small' : undefined}
						color="currentColor"
						label={formatMessage(messages.removeFromStarred)}
					/>
				) : (
					<UnstarredIcon
						LEGACY_size={isSmall ? 'small' : undefined}
						color="currentColor"
						label={formatMessage(messages.addToStarred)}
					/>
				)}
			</FavIconWrapper>
		);

		const renderIcon = () => {
			const label = isFavorite
				? formatMessage(messages.removeFromStarred)
				: formatMessage(messages.addToStarred);

			const StarIconComponent = isFavorite ? StarredIcon : UnstarredIcon;

			return (
				<StarIconComponent color="currentColor" label={label} size={isSmall ? 'small' : 'medium'} />
			);
		};

		const buttonContent = (
			<FavIconWrapper
				isFavorite={isFavorite}
				isAnimating={isAnimating}
				data-testid={iconWrapperTestId}
				isSmall={isSmall}
			>
				{renderIcon()}
			</FavIconWrapper>
		);

		const tooltipContent = isFavorite
			? formatMessage(messages.removeFromStarred)
			: formatMessage(messages.addToStarred);

		const ariaLabel = favoriteItemName
			? formatMessage(messages.toggleStarredWithItemName, { itemName: favoriteItemName })
			: formatMessage(messages.toggleStarred);

		return (
			<FavoriteButtonWrapper
				role="presentation"
				onClick={this.onBtnClickWrapper}
				onKeyPress={this.onBtnClickWrapper}
				data-is-favorite={isFavorite}
			>
				<Tooltip
					content={!hideTooltip && tooltipContent}
					hideTooltipOnClick
					position={tooltipPosition}
				>
					<Button
						appearance={isShownInList ? 'subtle-link' : 'subtle'}
						spacing="none"
						aria-label={ariaLabel}
						aria-pressed={isFavorite}
						onClick={!pending ? this.onClick : noop}
					>
						{isVisualRefreshEnabled() ? buttonContent : buttonContentOld}
					</Button>
				</Tooltip>
			</FavoriteButtonWrapper>
		);
	}
}

export default flow(
	withFireUiAnalytics({
		onClick: () => ({
			name: 'star',
		}),
	}),
	injectIntl,
)(FavoriteButtonStateless);

const animationTime = 500;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const FavoriteButtonWrapper = styled.div({
	display: 'inline-block',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FavoriteIconWrapperBaseOld = styled.span<{
	isSmall?: boolean;
	isFavorite?: boolean;
	isAnimating?: boolean;
}>(
	{
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		width: (props) => (props.isSmall ? '24px' : '32px'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		height: (props) => (props.isSmall ? '24px' : '32px'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) =>
		props.isAnimating &&
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		css({
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
			'@keyframes energyPop': {
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
				'0%': {
					// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
					color: '#253858',
					backgroundColor: 'transparent',
					transform: 'scale3d(0.5, 0.5, 0.5)',
				},
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
				'20%': {
					// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
					color: '#fffae6',
					// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
					backgroundColor: 'rgba(255, 171, 0, 0.2)',
					transform: 'scale3d(1, 1, 1)',
				},
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
				'80%': {
					backgroundColor: 'transparent',
					transform: 'scale3d(1.2, 1.2, 1.2)',
				},
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
				'100%': {
					backgroundColor: 'transparent',
					color: 'currentColor',
					transform: 'none',
				},
			},
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			svg: {
				borderRadius: token('radius.full'),
				animation: `energyPop ${animationTime / 1000}s ease`,
				animationFillMode: 'forwards',
				willChange: 'transform',
			},
		}),
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FavoriteIconWrapperBaseNew = styled.span<{
	isSmall?: boolean;
	isFavorite?: boolean;
	isAnimating?: boolean;
}>(
	{
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		width: (props) => (props.isSmall ? '24px' : '32px'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		height: (props) => (props.isSmall ? '24px' : '32px'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) =>
		props.isAnimating &&
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		css({
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
			'@keyframes energyPop': {
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
				'0%': {
					// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
					color: '#253858',
					backgroundColor: 'transparent',
					transform: 'scale3d(0.5, 0.5, 0.5)',
				},
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
				'20%': {
					// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
					color: '#fffae6',
					// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
					backgroundColor: 'rgba(255, 171, 0, 0.2)',
					transform: 'scale3d(1, 1, 1)',
				},
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
				'80%': {
					backgroundColor: 'transparent',
					transform: 'scale3d(1.2, 1.2, 1.2)',
				},
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
				'100%': {
					backgroundColor: 'transparent',
					color: 'currentColor',
					transform: 'none',
				},
			},
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			svg: {
				animation: `energyPop ${animationTime / 1000}s ease`,
				animationFillMode: 'forwards',
				willChange: 'transform',
			},
		}),
);

const FavoriteIconWrapperBase = componentWithCondition(
	isVisualRefreshEnabled,
	FavoriteIconWrapperBaseNew,
	FavoriteIconWrapperBaseOld,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FavIconWrapper = styled(FavoriteIconWrapperBase)<{
	isSmall?: boolean;
	isFavorite?: boolean;
	isAnimating?: boolean;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: (props) =>
		props.isFavorite ? token('color.icon.accent.yellow') : token('color.icon.subtle'),
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		color: (props) =>
			props.isFavorite ? token('color.background.accent.yellow.bolder') : token('color.icon'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const FavoriteButtonSkeleton = styled.div<{ isSmall?: boolean }>({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: (props) => (props.isSmall ? '24px' : '32px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: (props) => (props.isSmall ? '24px' : '32px'),
});
